import { useEffect } from 'react';
import { useRecoilValue, useResetRecoilState, useSetRecoilState } from 'recoil';
import clone from 'ramda/es/clone';
import {
  clinicalStepsBaseline,
  getStepsFromVersioned,
  steppedHPConfigurations,
  unsavedClinicalStepEdits,
  type UnsavedStepEditsRecord,
} from '../state';
import type { StepConfiguration } from '../steppedHPTypes';
import { isStepEqual } from './useClinicalStepDirtyState';

// An atomFamily setter needs a key, so the hook falls back to this when there is no HP to mirror. The
// effect never writes in that case, so nothing is ever stored under it.
const NO_HP_KEY = '__no_hp_key__';

/**
 * Picks out the steps that diverge from their baseline, with the baseline entries they diverge from.
 * Returns null when there is nothing unsaved, which the caller turns into a removal rather than an
 * empty record, so a clean HP leaves nothing on disk.
 *
 * Mirrors useClinicalStepDirtyState's matching rules deliberately: match by smid, skip a step with no
 * smid (newly added, not yet persisted) and a step absent from the baseline (pending creation). Neither
 * can be re-identified against a server response after a reload, so persisting them would store an
 * edit that could never be restored. Exported for testing.
 */
export function collectUnsavedStepEdits(
  steps: Nullable<StepConfiguration[]>,
  baseline: Nullable<StepConfiguration[]>
): Nullable<UnsavedStepEditsRecord> {
  if (steps == null || baseline == null) return null;

  const baselineBySmid = new Map(
    baseline.filter((step) => step.smid != null).map((step) => [step.smid as string, step])
  );

  const edits: StepConfiguration[] = [];
  const baselines: StepConfiguration[] = [];
  for (const step of steps) {
    if (step.smid == null) continue;
    const baselineStep = baselineBySmid.get(step.smid);
    if (baselineStep == null) continue;
    if (isStepEqual(step, baselineStep)) continue;
    edits.push(clone(step));
    baselines.push(clone(baselineStep));
  }

  return edits.length > 0 ? { edits, baselines } : null;
}

/**
 * Avoids rewriting an identical record on every unrelated step-atom change. The persister debounces
 * writes but still hits IndexedDB on the leading edge, and a no-op set would also churn subscribers.
 *
 * Compares smid explicitly rather than leaning on isStepEqual alone, which ignores smid because dirty
 * detection has already matched on it by the time it is called.
 */
function isRecordEqual(
  a: Nullable<UnsavedStepEditsRecord>,
  b: Nullable<UnsavedStepEditsRecord>
): boolean {
  if (a === b) return true;
  if (a == null || b == null) return false;
  if (a.edits.length !== b.edits.length) return false;
  return a.edits.every(
    (step, index) =>
      step.smid === b.edits[index].smid &&
      isStepEqual(step, b.edits[index]) &&
      isStepEqual(a.baselines[index], b.baselines[index])
  );
}

/**
 * Mirrors the current unsaved clinical step edits into their persisted record, so a viewer refresh can
 * restore them (VX-4803).
 *
 * Derives from steppedHPConfigurations and clinicalStepsBaseline, both already broadcast-synced, so
 * every viewer window computes the same record and they converge without the record itself carrying a
 * broadcast effect. Write debouncing lives in localForagePersisterEffect, so there is none here.
 */
export function useMirrorUnsavedStepEdits(hpKey: Nullable<string>): void {
  const versioned = useRecoilValue(steppedHPConfigurations);
  const baseline = useRecoilValue(clinicalStepsBaseline);
  const recordAtom = unsavedClinicalStepEdits(hpKey ?? NO_HP_KEY);
  const setRecord = useSetRecoilState(recordAtom);
  const resetRecord = useResetRecoilState(recordAtom);

  const steps = getStepsFromVersioned(versioned);

  useEffect(() => {
    if (hpKey == null) return;
    // Write nothing while the live atoms are still empty. On a reload this hook mounts before
    // useHangingProtocol has initialised either atom, so it would compute "nothing is dirty" and clear
    // the very record the init pass is about to read -- erasing the edits on every refresh. The same
    // holds after useClearSteppedHPState nulls both atoms; clearing the record there is that hook's
    // job, done explicitly against a known hpKey.
    if (steps == null || baseline == null) return;
    const unsaved = collectUnsavedStepEdits(steps, baseline);
    // Reset rather than set(null) when nothing is dirty. localForagePersisterEffect only removes the
    // IndexedDB entry on a reset; a stored null is a real value that occupies one of the family's
    // maxKeys retention slots and can evict another case's live unsaved edits. Recoil bails out of a
    // reset on an atom already at its default, so repeating this on a clean HP costs nothing.
    if (unsaved == null) {
      resetRecord();
      return;
    }
    setRecord((previous) => (isRecordEqual(previous, unsaved) ? previous : unsaved));
  }, [hpKey, steps, baseline, setRecord, resetRecord]);
}
